/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect } from 'react';
import { Button } from 'reactstrap';
import { useAppSelector } from '../../../reducers';
import { getCurrentUser } from '../../../selectors/user';
import { BulkActionUtr } from './BulkActionToolbar';
import './BulkActionToolbar.scss';

export interface ActionsToolbarProps {
  selectedQuestions: BulkActionUtr[];
  toggleSelectAll: () => void;
  handleClose: () => void;
  questionCount: number;
  buttons: {
    name: string;
    Component: React.ElementType;
    buttonProps: object;
  }[];
}

export const ActionsToolbar = (props: ActionsToolbarProps) => {
  const {
    selectedQuestions,
    toggleSelectAll,
    handleClose,
    questionCount,
    buttons = [],
  } = props;

  const currentUser = useAppSelector(getCurrentUser);
  const selectedUniqueCount = new Set(selectedQuestions.map((q) => q._id)).size;
  const className = selectedUniqueCount > 0 ? 'toolbarShow' : 'toolbarHide';

  useEffect(() => {
    const zE = window.zE;
    if (zE) {
      // Hide when selected count is more than 0
      zE('messenger:set', 'zIndex', selectedUniqueCount > 0 ? -1 : 1000);
      // Still need to restore the widget at the end
      return () => zE('messenger:set', 'zIndex', 1000);
    }
  }, [selectedUniqueCount]);

  if (!currentUser || buttons.length === 0) {
    return null;
  }

  const areAllSelected = questionCount === selectedUniqueCount;

  return (
    <div className={`bulk-action-toolbar ${className}`}>
      <div className='bulk-action-contents d-flex justify-content-center '>
        <div className='mr-auto'></div>
        <div>
          {buttons.map(({ name, Component, buttonProps }) => (
            <Component key={name} {...buttonProps} />
          ))}
        </div>
        <div className='ml-2 d-flex align-items-center flex-column flex-lg-row text-nowrap'>
          <div>
            <span className='p-1 text-ThemeTextWhite d-inline-block' style={{ minWidth: '80px' }}>
              {selectedUniqueCount} selected
            </span>
          </div>
          <div>
            <Button
              color='link'
              size='sm'
              className='text-ThemeTextWhite'
              onClick={toggleSelectAll}
              style={{ textDecoration: 'underline' }}
            >
              {areAllSelected ? 'Unselect all' : `Select all (${questionCount})`}
            </Button>
          </div>
        </div>
        <div className='ml-auto d-flex align-items-center'>
          <Button color='link' onClick={handleClose}>
            <i className='fa fa-times text-ColourWhite' />
          </Button>
        </div>
      </div>
    </div>
  );
};
